import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ApplicationContext } from '../../context/GlobalState';
import { t, localizedPath } from '../../i18n';
import { useLocale } from '../../context/LocaleContext';
import { getLocalizedDisplay } from '../../utils/localizedDisplay';
import HreflangTags from '../../components/HreflangTags/HreflangTags';

export default function Services() {
  const { state } = useContext(ApplicationContext);
  const { locale } = useLocale();
  const brand = state.brand;
  const baseDomain = brand?.domain || 'gaspineortho.com';
  const brandKey = brand?.brandKey || 'gso';
  const services = state.taxonomy?.dimensions?.find(d => d.id === 'service')?.values || [];

  const categories = {};
  services.forEach(svc => {
    const cat = svc.category || 'general';
    if (!categories[cat]) categories[cat] = [];
    categories[cat].push(svc);
  });

  const catLabels = {
    'accident-recovery': '🚗 Accident Recovery',
    'workers-comp': '🏗️ Workers\' Compensation',
    'injury-legal': '⚖️ Injury & Legal',
    'surgical': '🔬 Surgical',
    'rehabilitation': '💪 Rehabilitation',
    'treatment': '💊 Treatment',
    'diagnostic': '🩻 Diagnostic',
    'legal-support': '📋 Legal Support',
    'condition': '🩹 Conditions',
    'general': '🏥 General',
  };

  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    name: brand.name,
    url: `https://${baseDomain}/services`,
    telephone: brand.phone,
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Medical Services",
      itemListElement: services.slice(0, 20).map(s => ({
        "@type": "Offer",
        itemOffered: { "@type": "MedicalTherapy", name: s.display }
      }))
    }
  };

  return (
    <div className="services-index">
      <Helmet>
        <title>{brand.name} Services | Orthopedic & Injury Care</title>
        <meta name="description" content={`Explore medical services at ${brand.name}. Orthopedic surgery, pain management, physical therapy, car accident treatment, and more.`} />
        <meta property="og:title" content={`${brand.name} Services`} />
        <meta property="og:description" content={`Explore our medical services at ${brand.name}. Expert orthopedic and injury care.`} />
        <meta property="og:url" content={`https://${baseDomain}/services`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`https://${baseDomain}/assets/brand/${brandKey}/logo-color.webp`} />
        <link rel="canonical" href={`https://${baseDomain}/services`} />
      </Helmet>
      <HreflangTags baseDomain={baseDomain} pathWithoutLocale="/services" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(servicesSchema) }} />
      <section className="glass-dark" style={{ padding: '60px 0', textAlign: 'center', borderRadius: 0, marginBottom: 40 }}>
        <div className="container">
          <h1>{t('nav.services', locale)}</h1>
          <p style={{ opacity: 0.9 }}>{brand.name}</p>
        </div>
      </section>
      <section className="container" style={{ maxWidth: 1000, margin: '0 auto', padding: '0 20px 60px' }}>
        {Object.entries(categories).map(([cat, svcs]) => (
          <div key={cat} style={{ marginBottom: 32 }}>
            <h2 style={{ marginBottom: 16 }}>{catLabels[cat] || cat}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {svcs.map((svc, i) => (
                <Link key={i} to={localizedPath(`/${brand.locations?.[0] || 'atlanta'}/${svc.slug}`, locale)} className="glass-card" style={{ padding: 20, textDecoration: 'none', color: 'var(--color-text)' }}>
                  <h3 style={{ fontSize: '1.1rem', marginBottom: 4 }}>{getLocalizedDisplay(svc, locale)}</h3>
                  <span style={{ color: 'var(--color-accent)', fontSize: '0.9rem' }}>{t('cta.learnMore', locale)} →</span>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
